// Algolia-driven replacement for the native <collection-product-grid>
// (assets/theme.js). The UI plumbing below (filter drawer, sticky
// filter/sort button, sort dropdowns, header/announcement height tracking)
// is adapted from CollectionProductGrid so the section's Liquid/CSS keep
// working unmodified; only the data-fetching seam (handleFilterSortFormChange)
// is replaced, with Algolia providing product selection/order/facet counts
// instead of a Shopify section_id re-render.
//
// Deliberately NOT pixel-perfect versus the native flow - see
// algolia-product-tile.js and algolia-filter-mapping.js for the specific,
// accepted simplifications (no swatches/ratings, plain quick-add button,
// static price-slider bounds, simplified pagination windowing, "manual"/
// title sort falls back to relevance).
import { AlgoliaClient, publishAlgoliaEvent } from "./algolia-client.js";
import {
  shopifyParamsToAlgolia,
  baseCollectionFilters,
  combineFilters,
  indexForSort,
  facetForParamName,
  algoliaFacetParamName,
  humanizeFacetLabel,
  EXCLUDED_EXTRA_FACET_ATTRIBUTES,
} from "./algolia-filter-mapping.js";
import { renderProductTile, bindQuickAddDelegate } from "./algolia-product-tile.js";
import { E as EVENTS } from "./events-58bc9098.js";

// Deliberately NOT imported from algolia-product-tile.js: a cross-file
// export dependency here means a partial deploy (this file updated, that
// one not) throws a fatal "does not provide an export" error at module
// load and silently breaks the whole component (skeleton stuck forever,
// connectedCallback never runs) - not worth the risk for one helper.
function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

// Same "duplicate rather than import" reasoning as escapeHtml above -
// used by renderCollectionResults/renderArticleResults (search page only).
function imageUrlWithWidth(url, width) {
  if (!url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("width", String(width));
    return u.toString();
  } catch (e) {
    return url;
  }
}

// Deliberately defined here, NOT imported from algolia-filter-mapping.js -
// same partial-deploy hazard as escapeHtml/imageUrlWithWidth above, but worse:
// this file's <script src> gets a fresh ?v= cache-buster on every theme
// publish, but its *relative* `import "./algolia-filter-mapping.js"` resolves
// to an un-versioned URL that the CDN/browser caches indefinitely. So a newly
// added export there can stay invisible for a long time after publishing,
// throwing "does not provide an export named 'appliedFacetFilters'" at module
// load and bricking the whole grid (skeleton stuck forever). Keeping this
// local - it only needs facetForParamName, which already existed in the
// mapping file's exports - means the grid never asks that cached file for
// anything new. Returns the applied discrete facet filters as Algolia Insights
// "facet:value" strings (what clickedFilters expects) - one plain entry per
// selected value (opaque labels there, not parsed, so no quoting). Price
// bounds are a slider drag, not a facet click, so excluded; availability is a
// discrete on/off facet, included.
function appliedFacetFilters(searchParams) {
  const params = new URLSearchParams(searchParams);
  const filters = [];
  for (const [key, value] of params.entries()) {
    if (key === "sort_by" || key === "q") continue;
    if (key === "filter.v.price.gte" || key === "filter.v.price.lte") continue;
    const facet = facetForParamName(key);
    if (!facet) continue;
    if (facet.type === "availability") {
      if (value === "1") filters.push("inventory_available:true");
      continue;
    }
    filters.push(`${facet.attribute}:${value}`);
  }
  return filters;
}

const {
  FILTER_REPAINT,
  FILTER_SORT_CHANGED,
  SORT_CHANGED,
  HEADER_HEIGHT_UPDATED,
  ANNOUNCEMENT_HEIGHT_UPDATED,
} = EVENTS;

function getMediaQuery(querySize) {
  const value = getComputedStyle(document.documentElement).getPropertyValue(`--js-${querySize}`);
  return value || false;
}

class AlgoliaCollectionProductGrid extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      activeFilters: "[js-active-filters]",
      header: "header-wrapper",
      announcementBar: "announcement-bar",
      filterDrawerTrigger: "[js-filter-drawer-trigger]",
      stickyFilterDrawerTrigger: "[js-filter-drawer-trigger--sticky]",
      filterDrawerHeading: "[js-filter-drawer-heading]",
      filterDrawerViewResults: "[js-filter-view-results]",
      filterDrawerViewResultsIdleLabel: "[js-filter-view-results-label]",
      filterGroup: "[js-filter-group]",
      filterGroupParent: "[js-filter-group-parent]",
      filterSortFormComponent: "[js-filter-sort-form-component]",
      footer: "footer",
      pagination: "[js-pagination]",
      // [js-result-grid]/[js-result-grid-top-bar] are the search results
      // page's equivalent hooks (sections/algolia-search-results.liquid) -
      // same component, reused as-is for both collection and search.
      productGrid: "[js-product-grid], [js-result-grid]",
      sortDrawerTrigger: "[js-sort-drawer-trigger]",
      sortDropdowns: "[js-sort-top-bar], [js-sort-sticky-button], [js-sort-mobile-drawer]",
      sortTriggerValue: "[js-sort-trigger-value]",
      stickyFilterSort: "[js-sticky-filter-sort-button]",
      topBar: "[js-product-grid-top-bar], [js-result-grid-top-bar]",
      disclosureWrapper: ".disclosure-wrapper",
      extraFilters: "[js-algolia-extra-filters]",
      // Search results page only (sections/algolia-search-results.liquid) -
      // absent (and harmless, all lookups below no-op) on collection pages.
      collectionResults: "[js-algolia-collection-results]",
      articleResults: "[js-algolia-article-results]",
    };

    // Cap on how many values to render per client-generated facet group -
    // some (e.g. named_tags.INGREDIENT) have 100+ distinct values; showing
    // only the most common ones keeps the drawer usable.
    this.maxFacetValues = 20;

    this.classes = {
      active: "active",
      hidden: "hidden",
      loading: "loading",
      visible: "visible",
    };

    this.ids = { filterDrawer: "filterDrawer", mobileSortDrawer: "mobileSortDrawer" };
    this.breakpoints = { smallDesktop: getMediaQuery("small-desktop") };

    this.events = {
      openFilterDrawer: new CustomEvent(EVENTS.MODAL_POPUP_OPEN(this.ids.filterDrawer)),
      closeFilterDrawer: new CustomEvent(EVENTS.MODAL_POPUP_CLOSE(this.ids.filterDrawer)),
      openSortDrawer: new CustomEvent(EVENTS.MODAL_POPUP_OPEN(this.ids.mobileSortDrawer)),
      closeSortDrawer: new CustomEvent(EVENTS.MODAL_POPUP_CLOSE(this.ids.mobileSortDrawer)),
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    const eventOptions = { signal: this.controller.signal };
    this.eventOptions = eventOptions;

    this.config = JSON.parse(this.querySelector("[js-algolia-grid-config]").textContent);
    this.client = new AlgoliaClient({ appId: this.config.appId, searchKey: this.config.searchKey });

    this.sectionId = this.dataset.sectionId;
    this.header = document.querySelector(this.selectors.header);
    this.announcementBar = document.querySelector(this.selectors.announcementBar);
    this.headerHeight = 0;
    this.announcementHeight = 0;
    this.getHeaderAnnouncementHeight();
    document.addEventListener(HEADER_HEIGHT_UPDATED, this.handleHeaderHeightChange.bind(this), eventOptions);
    document.addEventListener(ANNOUNCEMENT_HEIGHT_UPDATED, this.handleAnnouncementHeightChange.bind(this), eventOptions);

    this.filterDrawerTriggers = this.querySelectorAll(`${this.selectors.filterDrawerTrigger}, ${this.selectors.stickyFilterDrawerTrigger}`);
    this.filterDrawerHeading = this.querySelector(this.selectors.filterDrawerHeading);
    this.filterDrawerViewResults = this.querySelector(this.selectors.filterDrawerViewResults);
    this.filterDrawerViewResultsIdleLabels = this.querySelectorAll(this.selectors.filterDrawerViewResultsIdleLabel);
    this.activeFilters = this.querySelector(this.selectors.activeFilters);
    this.filterGroups = this.querySelectorAll(this.selectors.filterGroup);
    this.extraFiltersContainer = this.querySelector(this.selectors.extraFilters);
    this.collectionResultsContainer = this.querySelector(this.selectors.collectionResults);
    this.articleResultsContainer = this.querySelector(this.selectors.articleResults);
    this.activeFilters?.addEventListener("click", this.handleActiveFilterRemove.bind(this), eventOptions);
    this.topBar = this.querySelector(this.selectors.topBar);
    this.sortTriggerValue = this.querySelector(this.selectors.sortTriggerValue);
    this.sortDropdowns = this.querySelectorAll(this.selectors.sortDropdowns);
    this.productGrid = this.querySelector(this.selectors.productGrid);
    this.filterSortFormComponent = this.querySelector(this.selectors.filterSortFormComponent);
    this.pagination = this.querySelector(this.selectors.pagination);

    this.initFilterDrawerTriggers();
    this.filterDrawerViewResults?.addEventListener("click", this.handleViewResultsClick.bind(this), eventOptions);
    this.addEventListener(FILTER_SORT_CHANGED, this.handleFilterSortFormChange.bind(this), eventOptions);
    this.initSortDropdowns();

    if (this.dataset.filterEnabled === "true" || this.dataset.sortEnabled === "true") {
      this.initStickyFilterSortButton();
    }

    this.sortDrawerTrigger?.addEventListener("click", this.openSortDrawer.bind(this), eventOptions);
    window.addEventListener(EVENTS.MODAL_POPUP_CLOSED(this.ids.mobileSortDrawer), this.showStickyFilterSort.bind(this), eventOptions);

    // Pagination links are plain hrefs today (full page reloads) - intercept
    // them so page changes stay inside the Algolia-driven flow instead of
    // reloading into a native (non-Algolia) render.
    this.pagination?.addEventListener("click", this.handlePaginationClick.bind(this), eventOptions);

    // Quick-add buttons are delegated once here rather than per tile, since
    // tiles get replaced wholesale on every query.
    bindQuickAddDelegate(this.productGrid);
    // Result click-throughs are no longer tracked here: they're captured by
    // GTM's "Algolia - result link click" trigger (Click - Just Links, Wait
    // for Tags), which reads queryID/objectID/position from the ?algolia_qid=
    // params renderProductTile puts on each link and fires
    // clickedObjectIDsAfterSearch. That path survives the click-through
    // navigation this pixel event was losing, so the local handler was
    // removed to avoid double-counting.

    this.currentPage = 1;
    this.currentSearchParams = new URLSearchParams(window.location.search).toString();

    // The grid's initial contents came from the native Liquid render (the
    // page just loaded normally) - fire an Algolia query once up front so
    // queryID/insights tracking is wired up even before the shopper touches
    // a filter, matching what InstantSearch would do on load.
    this.runQuery({ searchParams: this.currentSearchParams, page: 1, isInitialLoad: true });
  }

  initFilterDrawerTriggers() {
    this.filterDrawerTriggers.forEach((trigger) => {
      trigger.addEventListener("click", this.handleFilterDrawerOpen.bind(this), this.eventOptions);
    });
    window.addEventListener(EVENTS.MODAL_POPUP_CLOSED(this.ids.filterDrawer), this.handleFilterDrawerClose.bind(this), this.eventOptions);
  }

  initStickyFilterSortButton() {
    this.siteFooter = document.querySelector(this.selectors.footer);
    this.stickyFilterSort = this.querySelector(this.selectors.stickyFilterSort);
    this.sortDrawerTrigger = this.querySelector(this.selectors.sortDrawerTrigger)?.parentNode;

    // Below the small-desktop breakpoint the sticky button is the *only*
    // way to reach filtering/sorting (the inline top-bar controls are
    // display:none there - see .collection-top-bar__filter-button /
    // .collection-top-bar__sort-wrapper in theme.css), so it must show
    // immediately on load rather than waiting for the shopper to scroll
    // past the top bar first - it only hides once the footer scrolls into
    // view (nothing left to filter/sort).
    this.mobileFooterObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.mobileTopBarObserver?.disconnect();
          this.hideStickyFilterSort();
        } else {
          if (this.topBar) this.mobileTopBarObserver?.observe(this.topBar);
          this.showStickyFilterSort();
        }
      },
      { threshold: 0.05 },
    );

    if (this.topBar) {
      this.mobileTopBarObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top <= 0) this.showStickyFilterSort();
        else this.hideStickyFilterSort();
      });
    }

    // At/above the small-desktop breakpoint the inline top-bar controls are
    // already visible, so the sticky button there is only the optional
    // "Show sticky filter and sort button" convenience, gated behind
    // scrolling the top bar out of view.
    if (this.dataset.stickyFilterSortEnabled === "true" && this.topBar) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.topBarObserver?.disconnect();
            this.hideStickyFilterSort();
          } else {
            this.topBarObserver?.observe(this.topBar);
          }
        },
        { threshold: 0.1 },
      );

      this.topBarObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.topBar.classList.add(this.classes.visible);
            this.hideStickyFilterSort();
          } else {
            this.topBar.classList.remove(this.classes.visible);
            if (entry.boundingClientRect.top <= this.headerHeight + this.announcementHeight) this.showStickyFilterSort();
            else this.hideStickyFilterSort();
          }
        },
        { rootMargin: `-${this.headerHeight + this.announcementHeight}px 0px 0px 0px` },
      );
    }

    this.smallDesktopMediaQuery = this.breakpoints.smallDesktop ? window.matchMedia(this.breakpoints.smallDesktop) : null;
    this.smallDesktopMediaQuery?.addEventListener("change", this.handleWindowResize.bind(this), this.eventOptions);
    this.handleWindowResize();
  }

  handleWindowResize() {
    if (this.smallDesktopMediaQuery?.matches) {
      this.hideStickyFilterSort();
      this.mobileFooterObserver.disconnect();
      this.mobileTopBarObserver?.disconnect();

      if (this.siteFooter) this.footerObserver?.observe(this.siteFooter);
    } else {
      this.hideStickyFilterSort();
      this.footerObserver?.disconnect();
      this.topBarObserver?.disconnect();

      if (this.siteFooter) this.mobileFooterObserver.observe(this.siteFooter);
    }
  }

  handleFilterDrawerOpen(event) {
    this.hideStickyFilterSort();
    if (event.target.closest(this.selectors.stickyFilterDrawerTrigger)) {
      setTimeout(() => window.dispatchEvent(this.events.openFilterDrawer), 240);
    } else {
      window.dispatchEvent(this.events.openFilterDrawer);
    }
  }

  handleFilterDrawerClose() {
    // Below the small-desktop breakpoint the sticky button is the only way
    // back into filtering/sorting, so it always reappears - above it, only
    // when the merchant's sticky-button setting is on and the top bar (with
    // its own always-visible filter/sort controls) has scrolled out of view.
    if (!this.smallDesktopMediaQuery?.matches) {
      this.showStickyFilterSort();
    } else if (!this.topBar?.classList.contains(this.classes.visible) && this.dataset.stickyFilterSortEnabled === "true") {
      this.showStickyFilterSort();
    }
  }

  handleViewResultsClick() {
    window.dispatchEvent(this.events.closeFilterDrawer);
  }

  handlePaginationClick(event) {
    const link = event.target.closest("a");
    if (!link) return;
    event.preventDefault();

    const url = new URL(link.href, window.location.origin);
    const page = Number(url.searchParams.get("page")) || 1;
    url.searchParams.delete("page");

    this.runQuery({ searchParams: url.searchParams.toString(), page });
  }

  handleFilterSortFormChange(event) {
    const topBarScrollTop =
      this.topBar.getBoundingClientRect().top + window.scrollY - this.headerHeight - this.announcementHeight;
    window.scrollTo({ top: topBarScrollTop, behavior: "smooth" });

    this.updateURLHash(event.detail.searchParams);
    this.runQuery({ searchParams: event.detail.searchParams, page: 1, sortBy: event.detail.sortBy });
  }

  async runQuery({ searchParams, page, sortBy, isInitialLoad }) {
    this.classList.add(this.classes.loading);
    this.currentSearchParams = searchParams;
    this.currentPage = page;

    const { filters: userFilters, sortBy: parsedSortBy, query } = shopifyParamsToAlgolia(searchParams);
    const effectiveSortBy = sortBy ?? parsedSortBy;
    const indexName = indexForSort(this.config.indexName, effectiveSortBy);
    // baseCollectionFilters(collectionHandle) skips the collection-scoping
    // clause entirely when collectionHandle is blank - true on the search
    // results page (this.config.collectionHandle isn't set there), where
    // the query should run across the whole index instead of one collection.
    const filters = combineFilters(baseCollectionFilters(this.config.collectionHandle), userFilters);

    // Collections/articles are search-results-page-only extras (see
    // algolia-search-results.liquid) - config only carries these index
    // names there, so this is naturally a no-op on collection pages. Only
    // fired once, on the initial load: unlike products, the shopper never
    // refines collections/articles by facet/sort/page, so re-querying them
    // on every filter click or page change would just repeat the same
    // request for no benefit. An empty query (e.g. arriving on a bare
    // /search with just an active filter, no "q") has no meaningful
    // relevance signal for these two, so they're skipped in that case too.
    const requests = [
      {
        indexName,
        params: {
          // NOTE: clickAnalytics is deliberately NOT set here, unlike the
          // predictive drawer and Recommend, which set it per request. It is
          // enabled as an index-level default in the Algolia dashboard
          // (Configuration -> Query Parameters), which is why mainResult
          // .queryID below is populated even though nothing here asks for it.
          // Verified 2026-09-07 on a live collection page. Don't "fix" the
          // apparent omission without checking the dashboard first - and if
          // that default is ever turned off, every queryID on this surface
          // silently becomes undefined and all search attribution from the
          // collection and search-results grids stops.
          //
          // Only ever non-empty on the search results page - collection
          // pages have no free-text query, just filters/sort.
          query: query || "",
          filters,
          distinct: true,
          facets: ["*"],
          hitsPerPage: this.config.productsPerPage,
          page: page - 1,
        },
      },
    ];
    const fetchSecondaryResults = isInitialLoad && query && (this.config.collectionsIndexName || this.config.articlesIndexName);
    const collectionsRequestIndex = fetchSecondaryResults && this.config.collectionsIndexName ? requests.push({
      indexName: this.config.collectionsIndexName,
      params: { query, hitsPerPage: this.config.maxCollectionResults || 4, page: 0 },
    }) - 1 : null;
    const articlesRequestIndex = fetchSecondaryResults && this.config.articlesIndexName ? requests.push({
      indexName: this.config.articlesIndexName,
      params: { query, hitsPerPage: this.config.maxArticleResults || 4, page: 0 },
    }) - 1 : null;

    try {
      const results = await this.client.multiQuery(requests);
      const mainResult = results[0];

      this.currentQueryID = mainResult.queryID;
      this.currentIndexName = indexName;

      this.renderProductGrid(mainResult.hits, page);

      if (collectionsRequestIndex !== null) this.renderCollectionResults(results[collectionsRequestIndex].hits);
      if (articlesRequestIndex !== null) this.renderArticleResults(results[articlesRequestIndex].hits);

      // Reveals the real grid/pagination and hides the skeleton (see
      // component-algolia-product-tile.css) - only ever set once, the
      // first time real content is ready to show.
      if (isInitialLoad) this.dataset.algoliaReady = "true";

      if (this.dataset.filterEnabled === "true") {
        if (isInitialLoad) {
          this.renderExtraFacets(mainResult.facets || {});
          // renderExtraFacets adds new [js-filter-group] elements - the
          // querySelectorAll from connectedCallback is a static snapshot,
          // so it needs retaking to include them for patchFacetCounts below
          // (and every subsequent query).
          this.filterGroups = this.querySelectorAll(this.selectors.filterGroup);
        }
        this.patchFacetCounts(mainResult.facets || {});
        this.renderActiveFilters(searchParams);
        this.updateFilterViewResultsLabel(mainResult.nbHits);
      }
      this.renderPagination(mainResult.page, mainResult.nbPages);
      if (!isInitialLoad) this.updateSortDropdowns(effectiveSortBy);

      // ...AfterSearch (with queryID) rather than the generic viewedObjectIDs:
      // clicks/conversions are already tagged with this query's queryID, and
      // Algolia's Search Analytics (CTR, conversion rate, no-results rate per
      // query) is computed from the ...AfterSearch event family - an untagged
      // "viewed" event breaks that funnel at the first step regardless of how
      // well clicks/conversions are tracked.
      publishAlgoliaEvent("algolia_result_viewed", {
        index: indexName,
        queryID: mainResult.queryID,
        objectIDs: mainResult.hits.map((h) => h.objectID),
        positions: mainResult.hits.map((_, i) => i + 1),
      });

      // Algolia "clicked filters" insight. Only the filters newly applied
      // since the previous query are sent (the delta) - so adding a third
      // filter doesn't re-report the first two, and removing one reports
      // nothing (clickedFilters is for applying, not clearing). The initial
      // load just seeds the baseline: a facet already in the URL (a shopper
      // arriving on a pre-filtered link) wasn't clicked this session, so it
      // shouldn't count as a click. index is the base index, not the sort
      // replica - facet analytics aren't sort-scoped.
      const facetFilters = appliedFacetFilters(searchParams);

      // Algolia "viewed filters" insight: the FULL set of facets applied to
      // the results now on screen, published on every query including the
      // initial load - unlike clickedFilters above, which deliberately reports
      // only the delta. Algolia pairs viewedFilters with clickedFilters to work
      // out facet engagement, so viewed has to cover the pre-filtered arrival
      // (a shopper landing on a filtered link sees those facets even though
      // they never clicked them this session).
      //
      // Without this publish the GTM tag "Algolia - filters viewed" had no
      // source at all: it was bound to {{cjs - Algolia - facet filters
      // (viewed)}}, which never resolved, so every viewedFilters event Algolia
      // received carried an empty filter list.
      if (facetFilters.length) {
        publishAlgoliaEvent("algolia_result_filters_viewed", {
          index: this.config.indexName,
          filters: facetFilters,
        });
      }

      if (!isInitialLoad) {
        const previous = this.appliedFilters || [];
        const added = facetFilters.filter((f) => previous.indexOf(f) === -1);
        if (added.length) {
          publishAlgoliaEvent("algolia_result_filters_clicked", {
            index: this.config.indexName,
            filters: added,
          });
        }
      }
      this.appliedFilters = facetFilters;

      this.filterSortFormComponent?.dispatchEvent(new CustomEvent(FILTER_REPAINT, { detail: {} }));
    } catch (e) {
      console.error("[algolia-collection-grid] query failed", e);
      // Reveal the (empty/native) grid rather than leaving the skeleton
      // spinning forever if the very first query never resolves.
      if (isInitialLoad) this.dataset.algoliaReady = "true";
    } finally {
      this.classList.remove(this.classes.loading);
    }
  }

  renderProductGrid(hits, page) {
    const searchContext = this.currentQueryID
      ? { queryID: this.currentQueryID, indexName: this.currentIndexName }
      : undefined;
    const tiles = hits.map((hit, i) => renderProductTile(hit, this.config, i, searchContext));

    // Promotion-tile blocks only ever show on the first page, matching the
    // native grid's "paginate.current_page == 1" behavior. block.settings
    // .position is 1-indexed ("before the Nth product"), so it maps to
    // array index position - 1; positions past the end of this page are
    // dropped rather than shown at the tail, same as the native gate
    // ("position <= products_per_page and position <= paginate.items").
    if (page === 1 && this.config.promoTiles?.length) {
      this.config.promoTiles
        .slice()
        .sort((a, b) => a.position - b.position)
        .forEach((tile) => {
          const index = tile.position - 1;
          if (index >= 0 && index <= tiles.length) tiles.splice(index, 0, tile.html);
        });
    }

    this.productGrid.innerHTML = tiles.join("");
    // resultsEmptyClass lets the search results section (whose grid wrapper
    // uses search-result-grid__* class names, not collection-product-grid__*)
    // supply its own class name here - see algolia-search-results.liquid.
    this.productGrid.classList.toggle(
      this.config.resultsEmptyClass || "collection-product-grid__products--empty",
      hits.length === 0,
    );
  }

  // Search results page only (js-algolia-collection-results /
  // js-algolia-article-results - see algolia-search-results.liquid). Both
  // rows are hidden entirely when there are no hits, rather than shown
  // empty, since they're a secondary/supplementary result type below the
  // main product grid.
  //
  // hit.url is used when the Algolia record provides it directly (the
  // Shopify Algolia app's own indexing convention for non-product record
  // types, as best understood without a live record to confirm against -
  // see the console warning below for how to verify/correct this once
  // deployed) - constructing the path from handle alone is only attempted
  // as a fallback, and only for collections, where "/collections/<handle>"
  // is an unambiguous, fixed Shopify route. Articles need a blog handle
  // too, which isn't guessable, so an article hit with neither a `url`
  // field nor a `blog_handle` field renders as plain (non-clickable) text
  // rather than link to a guessed, likely-wrong URL.
  renderSecondaryResults(container, hits, { type, buildFallbackUrl }) {
    if (!container) return;
    const list = container.querySelector("[js-algolia-result-list]");
    if (!hits.length) {
      container.hidden = true;
      if (list) list.innerHTML = "";
      return;
    }

    container.hidden = false;
    list.innerHTML = hits
      .map((hit) => {
        const image = hit.image || hit.image_url;
        const imageHtml = image
          ? `<img src="${escapeHtml(imageUrlWithWidth(image, 300))}" alt="" loading="lazy" class="algolia-search-secondary-result__image">`
          : "";
        const titleHtml = `<span class="algolia-search-secondary-result__title fs-body-100">${escapeHtml(hit.title)}</span>`;
        const url = hit.url || buildFallbackUrl(hit);

        if (!url) {
          console.warn(
            `[algolia-collection-grid] ${type} hit "${hit.title}" (objectID ${hit.objectID}) has no usable url/handle field - check the record shape in Algolia and adjust renderSecondaryResults.`,
          );
          return `<span class="algolia-search-secondary-result algolia-search-secondary-result--no-link">${imageHtml}${titleHtml}</span>`;
        }

        return `<a href="${escapeHtml(url)}" class="algolia-search-secondary-result">${imageHtml}${titleHtml}</a>`;
      })
      .join("");
  }

  renderCollectionResults(hits) {
    this.renderSecondaryResults(this.collectionResultsContainer, hits, {
      type: "collection",
      buildFallbackUrl: (hit) =>
        hit.handle ? `${this.config.rootUrl.replace(/\/$/, "")}/collections/${hit.handle}` : null,
    });
  }

  renderArticleResults(hits) {
    this.renderSecondaryResults(this.articleResultsContainer, hits, {
      type: "article",
      buildFallbackUrl: (hit) =>
        hit.handle && hit.blog_handle
          ? `${this.config.rootUrl.replace(/\/$/, "")}/blogs/${hit.blog_handle}/${hit.handle}`
          : null,
    });
  }

  // Patches live counts/disabled-state into the *existing* filter-group
  // markup (both the native server-rendered ones and the extra ones this
  // element renders itself - see renderExtraFacets) rather than re-rendering
  // it, so accordion open/close state and scroll position survive a query.
  // New facet *values* that weren't part of the initial render still won't
  // appear until a full reload (a deliberate scope limit; see chat).
  patchFacetCounts(facets) {
    this.filterGroups.forEach((group) => {
      const inputs = group.querySelectorAll("input[name]");
      if (!inputs.length) return;

      const facet = facetForParamName(inputs[0].name);
      if (!facet || facet.type !== "facet" || !facets[facet.attribute]) return;

      inputs.forEach((input) => {
        const count = facets[facet.attribute][input.value] || 0;
        const li = input.closest(".filter-item");
        const countEl = li?.querySelector(".label-count");
        if (countEl) countEl.textContent = `(${count})`;
        // Never disable an input the shopper already has checked, even if
        // its own count comes back 0 for this combination of filters -
        // disabled inputs can't be unchecked by clicking them, so this was
        // the actual bug behind "filters stop responding after a few
        // clicks": checking a facet value whose count drops to 0 once
        // combined with something else locked the shopper out of ever
        // unchecking it again except via chip removal.
        const disable = count === 0 && !input.checked;
        input.disabled = disable;
        li?.classList.toggle("filter-item--disabled", disable);
      });
    });
  }

  // Client-rendered accordion filter-groups for Algolia facets not already
  // covered by a native Shopify collection filter (Price today - Brand/
  // Category used to be native too, but Shopify's filter values for those
  // don't match Algolia's real facet values, so they're rendered from here
  // instead - see algolia-collection-product-grid.liquid). Which attributes
  // render, and in what order, is entirely merchant-configured via
  // "algolia_facet" blocks (section.settings.facetOrder) - an attribute
  // with no block is simply never shown. Rendered once, from the initial
  // (unfiltered) facets response, then left in place - patchFacetCounts
  // keeps their counts live on every later query.
  renderExtraFacets(facets) {
    if (!this.extraFiltersContainer) return;

    const covered = new Set();
    this.filterGroups.forEach((group) => {
      const input = group.querySelector("input[name]");
      const facet = input && facetForParamName(input.name);
      if (facet?.type === "facet") covered.add(facet.attribute);
    });

    // Shopify's schema settings can't be populated from a live external API
    // call at theme-edit time, so a block's "Algolia attribute" field is
    // still free text - this is the guardrail for that: a typo'd/renamed
    // attribute doesn't just silently show nothing, it's called out by name
    // with what's actually available. renderFacetDiscoveryHelper below is
    // the other half - a theme-editor-only panel so the merchant never has
    // to type the name from memory in the first place.
    (this.config.facetOrder || []).forEach(({ attribute }) => {
      if (!EXCLUDED_EXTRA_FACET_ATTRIBUTES.has(attribute) && !facets[attribute]) {
        console.warn(
          `[algolia-collection-grid] Facet block attribute "${attribute}" has no data in Algolia's response (check for typos). Available: ${Object.keys(facets).join(", ")}`,
        );
      }
    });

    const html = (this.config.facetOrder || [])
      .filter(
        ({ attribute }) => facets[attribute] && !EXCLUDED_EXTRA_FACET_ATTRIBUTES.has(attribute) && !covered.has(attribute),
      )
      .map(({ attribute, label }) => {
        const values = Object.entries(facets[attribute])
          .sort((a, b) => b[1] - a[1])
          .slice(0, this.maxFacetValues);
        if (!values.length) return "";

        const paramName = algoliaFacetParamName(attribute);
        const items = values
          .map(([value, count]) => {
            const id = `algolia-filter--${attribute}-${value}`.replace(/[^a-zA-Z0-9_-]/g, "-");
            return `
              <li class="filter-item filter-item--type-checkbox">
                <div class="custom-input-wrapper">
                  <div class="custom-input custom-input--checkbox">
                    <div class="checkbox-wrapper">
                      <input type="checkbox" id="${id}" name="${paramName}" value="${value.replace(/"/g, "&quot;")}">
                      <div class="checkbox-checkmark"></div>
                    </div>
                    <label for="${id}"><span><span class="label-text">${value}</span> <span class="label-count">(${count})</span></span></label>
                  </div>
                </div>
              </li>
            `;
          })
          .join("");

        return `
          <accordion-group js-filter-group-parent>
            <accordion-item>
              <details class="accordion-item filter-group-wrapper" data-show-default-spacing="true" js-accordion-item>
                <summary class="accordion-item__title" js-accordion-item-summary>
                  ${label || humanizeFacetLabel(attribute)}
                  <span class="accordion-item-title__icon" style="--icon-width: 14px; --icon-height: 14px;">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 14" fill="none">
                      <g transform="rotate(0, 11, 7)">
                        <path d="M0.875 1.4375L11 11.5625L21.125 1.4375" stroke="currentColor" stroke-width="2.25"/>
                      </g>
                    </svg>
                  </span>
                </summary>
                <div class="accordion-item__content" js-accordion-item-content>
                  <ul class="filter-group filter-group--type-checkbox algolia-extra-facet__values" js-filter-group>${items}</ul>
                </div>
              </details>
            </accordion-item>
          </accordion-group>
        `;
      })
      .join("");

    this.extraFiltersContainer.innerHTML = html;
    this.renderFacetDiscoveryHelper(facets);
  }

  // Theme-editor-only "cheat sheet": Shopify section schema can't populate a
  // block setting's options from a live API call, so the "algolia_facet"
  // block's attribute field is free text - this lists every attribute
  // Algolia actually returned (with how many values/products it covers) so
  // the merchant can click-to-copy the exact string into a block instead of
  // typing it from memory. A checkmark marks ones already configured.
  // Invisible to shoppers - gated on Shopify.designMode.
  renderFacetDiscoveryHelper(facets) {
    if (!window.Shopify?.designMode || !this.extraFiltersContainer) return;

    const configured = new Set((this.config.facetOrder || []).map((f) => f.attribute));
    const attributes = Object.keys(facets).filter((attribute) => !EXCLUDED_EXTRA_FACET_ATTRIBUTES.has(attribute)).sort();

    if (!attributes.length) return;

    const rows = attributes
      .map((attribute) => {
        const valueCount = Object.keys(facets[attribute]).length;
        const isConfigured = configured.has(attribute);
        return `
          <button
            type="button"
            class="algolia-facet-discovery__attribute${isConfigured ? " algolia-facet-discovery__attribute--configured" : ""}"
            js-algolia-copy-facet-attribute
            data-attribute="${escapeHtml(attribute)}"
            title="Click to copy"
          >${escapeHtml(attribute)} (${valueCount})${isConfigured ? " &check;" : ""}</button>
        `;
      })
      .join("");

    const panel = document.createElement("div");
    panel.className = "algolia-facet-discovery";
    panel.innerHTML = `
      <p class="algolia-facet-discovery__heading">
        Theme editor only - available Algolia facet attributes (click to copy the exact name into a Facet block). &check; = already added.
      </p>
      <div class="algolia-facet-discovery__list">${rows}</div>
    `;
    panel.addEventListener("click", (event) => {
      const button = event.target.closest("[js-algolia-copy-facet-attribute]");
      if (!button) return;
      navigator.clipboard?.writeText(button.dataset.attribute).catch(() => {});
      button.classList.add("algolia-facet-discovery__attribute--copied");
      setTimeout(() => button.classList.remove("algolia-facet-discovery__attribute--copied"), 1200);
    });

    this.extraFiltersContainer.prepend(panel);
  }

  renderActiveFilters(searchParams) {
    const params = new URLSearchParams(searchParams);
    const chips = [];
    for (const [key, value] of params.entries()) {
      if (key === "sort_by") continue;
      const isPrice = key === "filter.v.price.gte" || key === "filter.v.price.lte";
      // Anything that isn't sort_by or a recognized filter param gets
      // silently ignored by shopifyParamsToAlgolia already (e.g. marketing/
      // analytics params like GA4's cross-domain "_gl" linker param, which
      // rides along in the URL whenever a shopper arrives via a cross-
      // domain-linked link) - this used to render a chip for literally any
      // leftover param regardless, showing a garbled "_gl" value as if it
      // were a selected filter. Gate on the same recognition logic the
      // query itself uses, so a chip only ever appears for something that
      // actually affects the results.
      if (!isPrice) {
        const facet = facetForParamName(key);
        if (!facet || facet.type !== "facet") continue;
      }
      // A price bound of <= 0 is treated as unset by shopifyParamsToAlgolia
      // (see algolia-filter-mapping.js) - skip it here too, otherwise a
      // leftover/degenerate value shows a phantom "Up to" chip with no
      // actual effect on the query.
      if (isPrice && Number(value) <= 0) continue;
      const label = isPrice ? `${key === "filter.v.price.gte" ? "From" : "Up to"} ${value}` : value;
      // Deliberately NOT js-active-filter-item: that hook is what
      // FilterSortForm's own handleFilterRemove binds to, and it expects
      // data-filter-label-handle/data-filter-value-label-handle (built from
      // the *filter's Liquid label*, which isn't available here) - using it
      // would attach a listener that throws on click. handleActiveFilterRemove
      // below does the equivalent job from name/value directly instead.
      chips.push(
        `<button type="button" class="active-filter-item" data-type="${isPrice ? "price-range" : "list"}" js-algolia-active-filter-item data-filter-remove-name="${key}" data-filter-remove-value="${value}">
          <span class="active-filter-item__label fs-body-100">${label}</span>
        </button>`,
      );
    }
    // this.activeFilters is [js-active-filters] - the *wrapper* the native
    // section only fills in with a "Selected filters" header + Clear all
    // button when active_filter_count > 0 at the initial server render.
    // Since that's frozen at page-load time, checking/unchecking anything
    // client-side always overwrote it with bare chips and no way to clear
    // them - there was no working "Clear all" at all once any interaction
    // happened. Render the equivalent structure here instead, every time.
    // js-clear-all-button is picked up automatically: FilterSortForm
    // rebinds to it on every FILTER_REPAINT (dispatched at the end of
    // runQuery) via its own initActiveFilterItems/handleFilterClear -
    // nothing extra needed here for the click itself to work.
    if (this.activeFilters) {
      this.activeFilters.innerHTML = chips.length
        ? `<div class="active-filters">
            <div class="active-filters__header">
              <span class="active-filters-header__heading fs-body-100">${this.config.strings.selectedFilters}</span>
              <button type="button" class="btn btn--style-text btn--size-regular btn--width-regular underlined" js-clear-all-button>${this.config.strings.clearAll}</button>
            </div>
            <div class="active-filters__list">${chips.join("")}</div>
          </div>`
        : "";
    }
    if (this.filterDrawerHeading) {
      this.filterDrawerHeading.textContent = this.filterDrawerHeading.textContent.replace(
        /\(\d+\)/,
        `(${chips.length})`,
      );
    }
  }

  // The "SHOW N RESULTS" button label is server-rendered once from
  // collection.products_count (the native, unfiltered total) - the native
  // flow refreshes it from a section_id re-render, which this Algolia-driven
  // flow never does, so it needs updating here from the live query instead.
  updateFilterViewResultsLabel(nbHits) {
    if (!this.filterDrawerViewResultsIdleLabels?.length) return;
    const { showResultsZero, showResultsOne, showResultsOther } = this.config.strings;
    const label =
      nbHits === 0 ? showResultsZero : nbHits === 1 ? showResultsOne : showResultsOther.replace("[[count]]", nbHits);
    this.filterDrawerViewResultsIdleLabels.forEach((el) => {
      el.textContent = label;
    });
  }

  handleActiveFilterRemove(event) {
    if (event.target.closest("[js-clear-all-button]")) {
      this.resetAllFilterInputs();
      return;
    }

    const chip = event.target.closest("[js-algolia-active-filter-item]");
    if (!chip) return;

    const { filterRemoveName: name, filterRemoveValue: value } = chip.dataset;
    const input = this.querySelector(`input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
    if (input) {
      input.checked = false;
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    // Price inputs have no fixed `value` to match on - clear directly.
    const priceInput = this.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (priceInput) {
      priceInput.value = "";
      priceInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  // FilterSortForm's own handleFilterClear (native, unmodified) only
  // fabricates an empty searchParams string for its dispatch - it never
  // actually unchecks any inputs, because the *native* flow immediately
  // follows it with a full section_id re-render that replaces the whole
  // form with fresh, correctly-unchecked HTML. This flow never does that,
  // so without this, "Clear all" reset the query/URL but left every
  // checkbox still visually (and functionally) checked - the very next
  // interaction's FormData read would resurrect all of them alongside
  // whatever was just clicked. The native dispatch already handles
  // resetting the actual query/URL; this just syncs the DOM to match.
  resetAllFilterInputs() {
    this.querySelectorAll('[js-filter-group] input[type="checkbox"], [js-filter-group] input[type="radio"]').forEach(
      (input) => {
        input.checked = false;
      },
    );
    this.querySelectorAll("[js-from-input], [js-to-input]").forEach((input) => {
      input.value = "";
    });
  }

  renderPagination(zeroIndexedPage, totalPages) {
    if (!this.pagination) return;
    const current = zeroIndexedPage + 1;
    if (totalPages <= 1) {
      this.pagination.innerHTML = "";
      return;
    }

    const buildLink = (page) => {
      const params = new URLSearchParams(this.currentSearchParams);
      params.set("page", String(page));
      return `${window.location.pathname}?${params.toString()}`;
    };

    const items = [];
    if (current > 1) {
      items.push(`<li class="pagination__item"><a class="pagination-item__navigation-button" href="${buildLink(current - 1)}">&larr;</a></li>`);
    }
    const start = Math.max(1, current - 2);
    const end = Math.min(totalPages, current + 2);
    for (let p = start; p <= end; p++) {
      items.push(
        `<li class="pagination__item pagination__item--type-${p === current ? "active" : "none"}"><a class="pagination-item__navigation-button" href="${buildLink(p)}">${p}</a></li>`,
      );
    }
    if (current < totalPages) {
      items.push(`<li class="pagination__item"><a class="pagination-item__navigation-button" href="${buildLink(current + 1)}">&rarr;</a></li>`);
    }

    this.pagination.innerHTML = `<div class="pagination pagination--alignment-center"><ul class="pagination__list ff-body">${items.join("")}</ul></div>`;
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  initSortDropdowns() {
    this.sortDropdowns.forEach((dropdown) => {
      dropdown.querySelectorAll("input").forEach((input) => {
        input.addEventListener("click", this.handleSortOptionClick.bind(this), this.eventOptions);
      });
    });
  }

  updateSortDropdowns(sortBy) {
    this.dataset.currentSort = sortBy;
    this.sortDropdowns.forEach((dropdown) => {
      const selected = dropdown.querySelector(`input[value="${sortBy}"]`);
      if (selected) selected.checked = true;
    });
  }

  handleSortOptionClick(event) {
    if (event.target.value === this.dataset.currentSort) return;
    const sortChangedEvent = new CustomEvent(SORT_CHANGED, { detail: { sortBy: event.target.value } });
    this.filterSortFormComponent.dispatchEvent(sortChangedEvent);
    if (this.sortTriggerValue) this.sortTriggerValue.innerText = event.target.dataset.label;

    const disclosureWrapper = event.target.closest(this.selectors.sortDropdowns)?.closest(this.selectors.disclosureWrapper);
    if (disclosureWrapper) disclosureWrapper.removeAttribute("open");
    else this.closeSortDrawer();
  }

  openSortDrawer() {
    this.hideStickyFilterSort();
    setTimeout(() => window.dispatchEvent(this.events.openSortDrawer), 240);
  }

  closeSortDrawer() {
    window.dispatchEvent(this.events.closeSortDrawer);
  }

  hideStickyFilterSort() {
    this.stickyFilterSort?.classList.remove(this.classes.active);
  }

  showStickyFilterSort() {
    this.stickyFilterSort?.classList.add(this.classes.active);
  }

  getHeaderAnnouncementHeight() {
    if (this.header?.dataset.enableStickyHeader === "true") {
      this.headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height").replace("px", ""), 10) || 0;
    }
    if (this.announcementBar?.hasAttribute("data-sticky")) {
      this.announcementHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--announcement-height").replace("px", ""), 10) || 0;
    }
  }

  handleHeaderHeightChange(event) {
    this.headerHeight = event.detail.height;
  }

  handleAnnouncementHeightChange(event) {
    this.announcementHeight = event.detail.height;
  }

  disconnectedCallback() {
    this.controller.abort();
    this.topBarObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.mobileTopBarObserver?.disconnect();
    this.mobileFooterObserver?.disconnect();
  }
}

if (!customElements.get("algolia-collection-product-grid")) {
  customElements.define("algolia-collection-product-grid", AlgoliaCollectionProductGrid);
}